/**
 * Service for managing Client Type Tags API operations
 * Implements all endpoints from the ClientTypeTags API Frontend Integration Guide
 */

import { makeAuthenticatedRequest } from './backendApiService';

const CLIENT_TYPE_TAGS_BASE_URL = '/api/clientTypeTags';

/**
 * Client Type Tags Service Class
 */
class ClientTypeTagsService {
    /**
     * Get all client type tag relationships
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of client type tag objects
     */
    async getAll(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, CLIENT_TYPE_TAGS_BASE_URL);
            return response;
        } catch (error) {
            console.error('Failed to fetch all client type tags:', error);
            throw error;
        }
    }

    /**
     * Get a specific client type tag relationship by IDs
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} clientTypeId - Client type ID
     * @returns {Promise<Object>} Client type tag object
     */
    async getByIds(accessToken, clientId, clientTypeId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/${clientId}/${clientTypeId}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to fetch client type tag for client ${clientId} and type ${clientTypeId}:`, error);
            throw error;
        }
    }

    /**
     * Get all client types assigned to a specific client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of client type tag objects
     */
    async getClientTypesForClient(accessToken, clientId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/client/${clientId}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to fetch client types for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get all clients assigned to a specific client type
     * @param {string} accessToken - JWT access token
     * @param {number} clientTypeId - Client type ID
     * @returns {Promise<Array>} Array of client type tag objects
     */
    async getClientsForClientType(accessToken, clientTypeId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/clientType/${clientTypeId}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to fetch clients for client type ${clientTypeId}:`, error);
            throw error;
        }
    }

    /**
     * Create a new client type tag relationship
     * @param {string} accessToken - JWT access token
     * @param {Object} data - Client type tag data
     * @param {number} data.clientId - Client ID
     * @param {number} data.clientTypeId - Client type ID
     * @returns {Promise<Object>} Created client type tag object
     */
    async create(accessToken, data) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, CLIENT_TYPE_TAGS_BASE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            return response;
        } catch (error) {
            console.error('Failed to create client type tag:', error);
            throw error;
        }
    }

    /**
     * Assign multiple client types to a client (creates new relationships)
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {Array<number>} clientTypeIds - Array of client type IDs
     * @returns {Promise<Array>} Array of created client type tag objects
     */
    async assignClientTypesToClient(accessToken, clientId, clientTypeIds) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/client/${clientId}/assign`, 
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(clientTypeIds)
                }
            );
            return response;
        } catch (error) {
            console.error(`Failed to assign client types to client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Assign multiple clients to a client type (creates new relationships)
     * @param {string} accessToken - JWT access token
     * @param {number} clientTypeId - Client type ID
     * @param {Array<number>} clientIds - Array of client IDs
     * @returns {Promise<Array>} Array of created client type tag objects
     */
    async assignClientsToClientType(accessToken, clientTypeId, clientIds) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/clientType/${clientTypeId}/assign`, 
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(clientIds)
                }
            );
            return response;
        } catch (error) {
            console.error(`Failed to assign clients to client type ${clientTypeId}:`, error);
            throw error;
        }
    }

    /**
     * Update (replace) all client type assignments for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {Array<number>} clientTypeIds - Array of client type IDs
     * @returns {Promise<Array>} Array of updated client type tag objects
     */
    async updateClientTypesForClient(accessToken, clientId, clientTypeIds) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/client/${clientId}`, 
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(clientTypeIds)
                }
            );
            return response;
        } catch (error) {
            console.error(`Failed to update client types for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Update (replace) all client assignments for a client type
     * @param {string} accessToken - JWT access token
     * @param {number} clientTypeId - Client type ID
     * @param {Array<number>} clientIds - Array of client IDs
     * @returns {Promise<Array>} Array of updated client type tag objects
     */
    async updateClientsForClientType(accessToken, clientTypeId, clientIds) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/clientType/${clientTypeId}`, 
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(clientIds)
                }
            );
            return response;
        } catch (error) {
            console.error(`Failed to update clients for client type ${clientTypeId}:`, error);
            throw error;
        }
    }

    /**
     * Delete a specific client type tag relationship
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} clientTypeId - Client type ID
     * @returns {Promise<void>}
     */
    async delete(accessToken, clientId, clientTypeId) {
        try {
            await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/${clientId}/${clientTypeId}`, 
                {
                    method: 'DELETE'
                }
            );
        } catch (error) {
            console.error(`Failed to delete client type tag for client ${clientId} and type ${clientTypeId}:`, error);
            throw error;
        }
    }

    /**
     * Delete all client type assignments for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<void>}
     */
    async deleteAllClientTypesForClient(accessToken, clientId) {
        try {
            await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/client/${clientId}`, 
                {
                    method: 'DELETE'
                }
            );
        } catch (error) {
            console.error(`Failed to delete all client types for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Delete all client assignments for a client type
     * @param {string} accessToken - JWT access token
     * @param {number} clientTypeId - Client type ID
     * @returns {Promise<void>}
     */
    async deleteAllClientsForClientType(accessToken, clientTypeId) {
        try {
            await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/clientType/${clientTypeId}`, 
                {
                    method: 'DELETE'
                }
            );
        } catch (error) {
            console.error(`Failed to delete all clients for client type ${clientTypeId}:`, error);
            throw error;
        }
    }

    /**
     * Check if a relationship exists between a client and client type
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} clientTypeId - Client type ID
     * @returns {Promise<boolean>} True if relationship exists
     */
    async checkRelationshipExists(accessToken, clientId, clientTypeId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/exists/${clientId}/${clientTypeId}`
            );
            return response.exists;
        } catch (error) {
            console.error(`Failed to check relationship existence for client ${clientId} and type ${clientTypeId}:`, error);
            throw error;
        }
    }

    /**
     * Get the count of client types assigned to a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<number>} Count of client types
     */
    async getClientTypeCountForClient(accessToken, clientId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/client/${clientId}/count`
            );
            return response.count;
        } catch (error) {
            console.error(`Failed to get client type count for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get the count of clients assigned to a client type
     * @param {string} accessToken - JWT access token
     * @param {number} clientTypeId - Client type ID
     * @returns {Promise<number>} Count of clients
     */
    async getClientCountForClientType(accessToken, clientTypeId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_TYPE_TAGS_BASE_URL}/clientType/${clientTypeId}/count`
            );
            return response.count;
        } catch (error) {
            console.error(`Failed to get client count for client type ${clientTypeId}:`, error);
            throw error;
        }
    }

    /**
     * Add a single client type to a client (if not already assigned)
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} clientTypeId - Client type ID
     * @returns {Promise<Object>} Created client type tag object
     */
    async addClientTypeToClient(accessToken, clientId, clientTypeId) {
        try {
            return await this.create(accessToken, { clientId, clientTypeId });
        } catch (error) {
            console.error(`Failed to add client type ${clientTypeId} to client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Remove a single client type from a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} clientTypeId - Client type ID
     * @returns {Promise<void>}
     */
    async removeClientTypeFromClient(accessToken, clientId, clientTypeId) {
        try {
            await this.delete(accessToken, clientId, clientTypeId);
        } catch (error) {
            console.error(`Failed to remove client type ${clientTypeId} from client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Toggle a client type assignment for a client (add if doesn't exist, remove if exists)
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {number} clientTypeId - Client type ID
     * @returns {Promise<Object>} Result object with action taken and data
     */
    async toggleClientTypeForClient(accessToken, clientId, clientTypeId) {
        try {
            const exists = await this.checkRelationshipExists(accessToken, clientId, clientTypeId);
            
            if (exists) {
                await this.removeClientTypeFromClient(accessToken, clientId, clientTypeId);
                return { action: 'removed', clientId, clientTypeId, exists: false };
            } else {
                const newTag = await this.addClientTypeToClient(accessToken, clientId, clientTypeId);
                return { action: 'added', data: newTag, exists: true };
            }
        } catch (error) {
            console.error(`Failed to toggle client type ${clientTypeId} for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get client type assignments with full client and type details
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of client type tag objects with detailed information
     */
    async getClientTypeAssignmentsWithDetails(accessToken, clientId) {
        try {
            const assignments = await this.getClientTypesForClient(accessToken, clientId);
            return assignments.map(assignment => ({
                ...assignment,
                displayName: assignment.clientType || `Type ${assignment.clientTypeId}`,
                displayDescription: assignment.clientTypeDescription || null
            }));
        } catch (error) {
            console.error(`Failed to get detailed client type assignments for client ${clientId}:`, error);
            throw error;
        }
    }
}

// Export singleton instance
export const clientTypeTagsService = new ClientTypeTagsService();

// Export individual methods for convenience
export const {
    getAll: getAllClientTypeTags,
    getByIds: getClientTypeTagByIds,
    getClientTypesForClient,
    getClientsForClientType,
    create: createClientTypeTag,
    assignClientTypesToClient,
    assignClientsToClientType,
    updateClientTypesForClient,
    updateClientsForClientType,
    delete: deleteClientTypeTag,
    deleteAllClientTypesForClient,
    deleteAllClientsForClientType,
    checkRelationshipExists: checkClientTypeRelationshipExists,
    getClientTypeCountForClient,
    getClientCountForClientType,
    addClientTypeToClient,
    removeClientTypeFromClient,
    toggleClientTypeForClient,
    getClientTypeAssignmentsWithDetails
} = clientTypeTagsService;

export default clientTypeTagsService;
